import { useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { List, LayoutGrid, GanttChart, Calendar, Plus, Info } from 'lucide-react';
import type { ProjectDetail } from '../services/projectService';
import { useIssues, useTransitionIssue } from '../hooks/useIssues';
import { useAllActiveWorkflowStatuses } from '../hooks/useResolvedWorkflow';
import { useIssueTypeWorkflowMappings, useWorkflowTransitions } from '../hooks/useWorkflows';
import type { WorkflowTransitionsData } from '../services/workflowService';
import type { ProjectRole } from '../components/board/boardUtils';
import QuickFilter, {
  EMPTY_QUICK_FILTER,
  buildTqlFromQuickFilter,
  type QuickFilterState,
} from '../components/issue/QuickFilter';
import TqlFilter from '../components/issue/TqlFilter';
import IssueListView from '../components/issue/IssueListView';
import IssueBoardView from '../components/issue/IssueBoardView';
import IssueTimelineView from '../components/issue/IssueTimelineView';
import IssueCalendarView from '../components/issue/IssueCalendarView';
import IssueCreateModal from '../components/issue/IssueCreateModal';
import { usePanelContext } from '../contexts/PanelContext';
import Spinner from '../components/ui/Spinner';
import type { IssueListItem } from '../services/issueService';
import { useAuthStore } from '../store/authStore';
import { useProjectIssueTypes } from '../hooks/useProjectIssueTypes';

type ViewMode = 'list' | 'board' | 'timeline' | 'calendar';

const VIEW_TABS: { mode: ViewMode; label: string; icon: React.ReactNode }[] = [
  { mode: 'timeline', label: '타임라인', icon: <GanttChart size={16} /> },
  { mode: 'board', label: '칸반보드', icon: <LayoutGrid size={16} /> },
  { mode: 'calendar', label: '캘린더', icon: <Calendar size={16} /> },
  { mode: 'list', label: '리스트', icon: <List size={16} /> },
];

export default function IssueListPage() {
  const { project } = useOutletContext<{ project: ProjectDetail }>();

  // CHG-043: STAKEHOLDER 읽기 전용
  const user = useAuthStore((s) => s.user);
  const myMembership = project.members.find((m) => m.user.id === user?.id);
  const isStakeholder = myMembership?.projectRole === 'STAKEHOLDER' && user?.role !== 'SUPER_ADMIN';
  const userProjectRole = (myMembership?.projectRole ?? null) as ProjectRole | null;
  const isSystemAdmin = user?.role === 'SUPER_ADMIN';

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const { openDetailPanel } = usePanelContext();

  // ?panel=ID 쿼리 → 사이드 패널 동기화는 PanelContext가 일괄 처리 (뒤로가기 복원 포함)
  const [showTql, setShowTql] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Filter state
  const [quickFilter, setQuickFilter] = useState<QuickFilterState>(EMPTY_QUICK_FILTER);
  const [tqlInput, setTqlInput] = useState('');
  const [activeTql, setActiveTql] = useState('');

  // Pagination and sort state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState('created:desc');

  // Build filter combining quick filters + TQL
  const effectiveFilter = useMemo(() => {
    const quickTql = buildTqlFromQuickFilter(quickFilter);
    if (activeTql && quickTql) return `${quickTql} AND ${activeTql}`;
    return activeTql || quickTql || undefined;
  }, [quickFilter, activeTql]);

  // Fetch issues - for board/calendar/timeline, fetch all without pagination
  const isBoardLike = viewMode !== 'list';
  const { data, isLoading } = useIssues(project.id, {
    filter: effectiveFilter,
    page: isBoardLike ? 1 : page,
    size: isBoardLike ? 500 : pageSize,
    sort,
  });

  const transitionMutation = useTransitionIssue(project.id);
  const { data: projectIssueTypes } = useProjectIssueTypes(project.id);

  // CHG-042: 다중 워크플로우 시 모든 활성 상태를 QuickFilter에 전달
  const { flatStatuses: allActiveStatuses, groups: workflowGroups, isMultiWorkflow } = useAllActiveWorkflowStatuses(project.id, project.workflow);
  const { data: mappingData } = useIssueTypeWorkflowMappings(project.id);

  // CHG-043: 워크플로우 전환 규칙 캐싱 (보드용)
  const { data: defaultTransitionData } = useWorkflowTransitions(project.workflow.workflowId);
  const transitionDataMap = useMemo(() => {
    const map = new Map<string, WorkflowTransitionsData>();
    if (defaultTransitionData) {
      map.set(project.workflow.workflowId, defaultTransitionData);
    }
    // Note: For multi-workflow, override workflow transition data would need separate queries.
    // Currently we support the default workflow's transition rules.
    // Override workflows will fall back to FREE mode behavior on the client.
    return map;
  }, [defaultTransitionData, project.workflow.workflowId]);

  // CHG-042: 이슈 유형 필터 → 단일 유형만 선택 시 해당 워크플로우 상태로 보드 컬럼 전환
  const boardWorkflowInfo = useMemo(() => {
    const selectedTypes = quickFilter.types;
    if (selectedTypes.length === 1 && mappingData) {
      // 단일 유형 선택 → 해당 유형의 워크플로우 상태 찾기
      const selectedType = selectedTypes[0];
      const override = mappingData.issueTypeWorkflows.find((m) => m.issueType === selectedType);
      const effectiveStatuses = override
        ? override.workflow.statuses
        : mappingData.defaultWorkflow.statuses;
      return {
        isMulti: false,
        statuses: effectiveStatuses,
      };
    }
    return {
      isMulti: isMultiWorkflow,
      statuses: project.workflow.statuses,
    };
  }, [quickFilter.types, mappingData, isMultiWorkflow, project.workflow.statuses]);

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? { page: 1, size: 25, total: 0, totalPages: 0 };

  // Handlers
  const handleIssueClick = useCallback((issue: IssueListItem) => {
    openDetailPanel('issue', issue.id);
  }, [openDetailPanel]);

  const handleTransition = useCallback(
    async (issueId: string, newStatusId: string) => {
      await transitionMutation.mutateAsync({ issueId, statusId: newStatusId });
    },
    [transitionMutation],
  );

  const handleExecuteTql = useCallback(() => {
    setActiveTql(tqlInput);
    setPage(1);
  }, [tqlInput]);

  const handleQuickFilterChange = useCallback(
    (val: QuickFilterState) => {
      setQuickFilter(val);
      setPage(1);
    },
    [],
  );

  const handlePageChange = useCallback((p: number) => setPage(p), []);
  const handlePageSizeChange = useCallback((s: number) => {
    setPageSize(s);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((s: string) => {
    setSort(s);
    setPage(1);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Page header — 고정 영역 */}
      <div className="flex items-center justify-between shrink-0 pb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">작업</h1>
          {/* View tabs */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.mode}
                onClick={() => setViewMode(tab.mode)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === tab.mode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
          {/* 칸반보드 다중 워크플로우 안내 — 서브탭 우측에 인라인 노출 */}
          {viewMode === 'board' && boardWorkflowInfo.isMulti && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md">
              <Info size={14} className="shrink-0" />
              여러 워크플로우가 사용 중입니다. 상태 카테고리 기준으로 표시됩니다.
            </div>
          )}
        </div>
        {!isStakeholder && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            작업 생성
          </button>
        )}
      </div>


      {/* 스크롤 영역 — 타임라인/리스트는 내부 컴포넌트만 스크롤, 그 외 모드는 외곽 스크롤 유지 */}
      <div className={`flex-1 min-h-0 flex flex-col space-y-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${viewMode === 'timeline' || viewMode === 'list' ? '' : 'overflow-y-auto'}`}>
      {/* 필터 바 — 기본/고급 전환 */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 shrink-0">
        {!showTql ? (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <QuickFilter
                members={project.members}
                workflowStatuses={allActiveStatuses.length > 0 ? allActiveStatuses : project.workflow.statuses}
                value={quickFilter}
                onChange={handleQuickFilterChange}
                projectIssueTypes={projectIssueTypes?.issueTypes}
              />
            </div>
            <button
              onClick={() => setShowTql(true)}
              className="shrink-0 px-2.5 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              TQL
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <TqlFilter
                tql={tqlInput}
                onTqlChange={setTqlInput}
                onExecute={handleExecuteTql}
                projectId={project.id}
                inline
              />
            </div>
            <button
              onClick={() => setShowTql(false)}
              className="shrink-0 px-2.5 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              기본
            </button>
          </div>
        )}
      </div>

      {/* Content area */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          {viewMode === 'list' && (
            <IssueListView
              items={items}
              pagination={pagination}
              page={page}
              pageSize={pageSize}
              sort={sort}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onSortChange={handleSortChange}
              onIssueClick={handleIssueClick}
            />
          )}
          {viewMode === 'board' && (
            <IssueBoardView
              items={items}
              workflowStatuses={boardWorkflowInfo.statuses}
              isMultiWorkflow={boardWorkflowInfo.isMulti}
              workflowGroups={workflowGroups}
              issueTypeWorkflows={mappingData?.issueTypeWorkflows ?? []}
              defaultWorkflowId={project.workflow.workflowId}
              onTransition={handleTransition}
              onIssueClick={handleIssueClick}
              readOnly={isStakeholder}
              transitionDataMap={transitionDataMap}
              userProjectRole={userProjectRole}
              isSystemAdmin={isSystemAdmin}
            />
          )}
          {viewMode === 'timeline' && (
            <IssueTimelineView
              projectId={project.id}
              items={items}
              onIssueSelect={(cuidId) => openDetailPanel('issue', cuidId)}
              projectIssueTypes={projectIssueTypes?.issueTypes}
            />
          )}
          {viewMode === 'calendar' && (
            <IssueCalendarView items={items} onIssueClick={handleIssueClick} />
          )}
        </>
      )}
      </div>

      {showCreateForm && (
        <IssueCreateModal
          projectId={project.id}
          members={project.members}
          projectIssueTypes={projectIssueTypes?.issueTypes}
          onClose={() => setShowCreateForm(false)}
        />
      )}
    </div>
  );
}
